'use client';

import React, { type CSSProperties } from 'react';
import dynamic from 'next/dynamic';
import { cn } from '@/lib/cn';
import type { Listing } from '@repo/api';
import { bathroomLabel } from '@repo/utils';
import { IconTrending, IconNew } from '@repo/svgs';
import { Rating } from '@/components/ui';
import Carousel from '../carousel/Carousel';
import { SaveListing } from '@/features/listings/SaveListing';
import { Image } from '@/components/ui/Image';
import { ListingCardTag } from '@/features/listings/ListingCardTag';
import { ResponsiveLink } from '@/components/shared/ResponsiveLink';
import { ListingPromotionPrice } from '@/features/listings/PriceDetailsTrigger';
import { useDeferredReady } from '@/hooks/useDeferredReady';
import { getListingUrgency } from '@/lib/listings/urgency';
import { useAnalytics } from '@/hooks/useAnalytics';
import type { EventName } from '@repo/analytics';

const CarouselDots = dynamic(
  () => import('../carousel/CarouselDots').then(mod => mod.CarouselDots),
  { loading: () => null },
);
const CarouselControls = dynamic(
  () => import('../carousel/CarouselControls').then(mod => mod.CarouselControls),
  { loading: () => null },
);

function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isIOS = /iP(hone|ad|od)/.test(ua);
  // iOS browsers include "Safari" in UA; exclude other shells.
  const isWebKit = /AppleWebKit/.test(ua);
  const isOtherShell = /(CriOS|FxiOS|EdgiOS|OPiOS)/.test(ua);
  return isIOS && isWebKit && !isOtherShell;
}

function getGpuCardVariant(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isIosSafari()) return false;

  // URL override for debugging: ?gpuCard=1 or ?gpuCard=0
  try {
    const sp = new URLSearchParams(window.location.search);
    const override = sp.get('gpuCard');
    if (override === '1') return true;
    if (override === '0') return false;
  } catch {
    // ignore
  }

  // Sticky A/B assignment per device.
  try {
    const key = 'ab:listingCard:transformGpu';
    const stored = window.localStorage.getItem(key);
    if (stored === 'on') return true;
    if (stored === 'off') return false;
    const on = Math.random() < 0.5;
    window.localStorage.setItem(key, on ? 'on' : 'off');
    return on;
  } catch {
    return false;
  }
}

export interface ListingCardProps {
  listing: Listing;
  isSaved?: boolean;
  showNetPrice?: boolean; // Controls strikethrough price display
  variant?: 'default' | 'map';
  className?: string;
  href?: string;
  showOnlyOneImage?: boolean;
  onSaveChange?: (saved: boolean) => void;
  /**
   * Controls whether the card link opens in a new tab.
   * - Default: opens in a new tab on desktop, same tab on mobile.
   */
  openInNewTab?: boolean;
  deferCarouselUntilIdle?: boolean;
  cardIndex?: number;
  /**
   * Fired the first time the renter navigates the card's images (desktop
   * prev/next arrows or mweb swipe) WITHOUT opening the unit. The surface
   * supplies the correct quickview analytics event; the card only detects the
   * image-navigation gesture. Fires once per image the renter navigates to.
   */
  onQuickView?: () => void;
  /**
   * Declarative quickview for SERVER-component parents that can't pass a
   * function. The card fires this event (via useAnalytics) on image navigation.
   * Ignored when `onQuickView` is provided.
   */
  quickViewEvent?: EventName;
  quickViewProps?: Record<string, unknown>;
}

export function ListingCard({
  listing,
  isSaved = false,
  showNetPrice = false,
  variant = 'default',
  className,
  href,
  showOnlyOneImage = false,
  onSaveChange,
  openInNewTab,
  deferCarouselUntilIdle = true,
  cardIndex,
  onQuickView,
  quickViewEvent,
  quickViewProps,
  ...props
}: ListingCardProps) {
  const analytics = useAnalytics();
  // Quickview: fire each time the renter navigates to a DIFFERENT image (desktop
  // prev/next arrows, dots, or mweb swipe) without opening the unit. Both funnel
  // through the carousel's `carousel-update` event (see CarouselClient), which
  // reports `currentIndex`. The listener is bound once per carousel id and reads
  // the latest `onQuickView` from a ref, so an inline callback re-rendering the
  // card can't re-register the listener or cause a double-fire. `lastIndex`
  // guards against the many same-index updates dispatched during a single
  // scroll animation. Arrow/dot clicks are ignored by the card-tap delegation
  // (they carry `data-analytics-skip`) and swipes don't click the link, so a
  // quickview never coincides with the card-tap event.
  const fireQuickViewRef = React.useRef<() => void>(() => {});
  React.useEffect(() => {
    fireQuickViewRef.current = () => {
      if (onQuickView) {
        onQuickView();
      } else if (quickViewEvent) {
        analytics.track(quickViewEvent, (quickViewProps ?? {}) as never);
      }
    };
  }, [onQuickView, quickViewEvent, quickViewProps, analytics]);
  React.useEffect(() => {
    const id = `listing-${listing.id}`;
    let lastIndex = 0;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail || detail.carouselId !== id) return;
      const idx = typeof detail.currentIndex === 'number' ? detail.currentIndex : 0;
      if (idx !== lastIndex) {
        lastIndex = idx;
        fireQuickViewRef.current?.();
      }
    };
    document.addEventListener('carousel-update', handler as EventListener);
    return () => document.removeEventListener('carousel-update', handler as EventListener);
  }, [listing.id]);
  const isDeferredReady = useDeferredReady({
    skip: !deferCarouselUntilIdle,
    delayMs: 4000,
    scope: 'listing-card',
  });

  // iOS Safari (especially inside transformed parents like drawers) can "delay paint" absolutely-positioned
  // overlay UI until the first user interaction. Forcing a dedicated composited layer fixes that.
  const overlayPaintStyle = React.useMemo(
    () =>
      ({
        WebkitTransform: 'translateZ(0)',
        transform: 'translateZ(0)',
        willChange: 'transform',
      }) as CSSProperties,
    [],
  );
  const enableCardTransformGpu = React.useMemo(() => getGpuCardVariant(), []);

  const imagesToUse = listing.images;
  const PLACEHOLDER_SRC = '/listing-card-placeholder.png';
  let carouselImages: { src: string; alt: string }[] = [];
  if (!imagesToUse?.length) {
    carouselImages = [
      {
        src: PLACEHOLDER_SRC,
        alt: 'Listing Card Placeholder',
      },
    ];
  } else {
    // Filter out missing/broken image URLs so we don't end up with a "carousel" of placeholders.
    const valid = imagesToUse
      .map((img, index) => {
        const src = img?.imageM || img?.imageS || img?.url;
        if (!src) return null;
        return {
          src,
          alt: img?.imageDescription || `${listing.fullAddress} - Image ${index + 1}`,
        };
      })
      .filter(Boolean) as { src: string; alt: string }[];

    carouselImages =
      valid.length > 0
        ? valid
        : [
            {
              src: PLACEHOLDER_SRC,
              alt: 'Listing Card Placeholder',
            },
          ];
  }

  // Format property details
  const propertyDetails = `${listing.numBedrooms === 0 ? 'Studio' : `${listing.numBedrooms} bed${listing.numBedrooms !== 1 ? 's' : ''}`}, ${bathroomLabel(listing.numBathrooms ?? 0, listing.numHalfBathrooms ?? 0)}`;

  // Format price

  const hasReviews = listing.buildingAverageRating !== null && listing.buildingReviewCount !== null;

  // Urgency tag ("Trending" / "New") — see getListingUrgency for the shared rule.
  const urgency = getListingUrgency(listing);
  const urgencyTag =
    urgency === 'trending'
      ? { Icon: IconTrending, label: 'Trending' }
      : urgency === 'new'
        ? { Icon: IconNew, label: 'New' }
        : null;

  // Build unit detail page URL
  const city = 'nyc';
  const unitUrl = href ?? `/unit/${city}/${listing.slug}/${listing.addressSlug}`;

  // Variant-specific styles
  const isMapVariant = variant === 'map';
  // Always render <Carousel> but start with a single image; expand after idle
  // so the first slide's DOM node stays in place (no unmount/remount flicker).
  const showAllImages = !showOnlyOneImage && isDeferredReady;
  const hasMultipleImages = carouselImages.length > 1;
  const visibleImages = showAllImages ? carouselImages : carouselImages.slice(0, 1);

  return (
    <ResponsiveLink
      href={unitUrl}
      openInNewTabOnDesktop={openInNewTab ?? true}
      className={cn(
        // Layout
        'group flex flex-col w-full',
        // Variant-specific spacing
        isMapVariant ? 'gap-2' : 'gap-2',
        // Map variant gets card styling
        isMapVariant && 'bg-primary rounded-2xl shadow-lg overflow-hidden',
        className,
      )}
      {...props}
    >
      {/* Image Carousel Container */}
      <div
        className={cn(
          // Positioning & bg
          // iOS Safari: ensure overlay UI (tags/save) paints reliably on first render inside transformed parents (e.g. drawers)
          // Avoid promoting every card to its own GPU layer (hurts drawer drag performance on iOS).
          // Overlay layer itself is already composited via `overlayPaintStyle`.
          'relative bg-primary isolate',
          enableCardTransformGpu && 'transform-gpu',
          // Shape & overflow - only apply to default variant
          !isMapVariant && 'rounded-2xl overflow-hidden shadow-md',
          // Map variant has no container styling (card handles it)
          isMapVariant && 'overflow-hidden',
        )}
      >
        <Carousel
          className={cn(
            // Relative for children overlays
            'relative z-0',
          )}
          carouselId={`listing-${listing.id}`}
          shouldFreeScroll={false}
          items={visibleImages.map((image, index) => ({
            id: image.src + index,
            content: (
              <Image
                aspectRatio={isMapVariant ? '16:9' : '3:2'}
                {...(cardIndex !== undefined && cardIndex < 8 && index === 0 && { priority: true })}
                src={image.src}
                alt={image.alt}
                quality={75}
                sizes="(max-width: 767px) 92vw, (max-width: 1023px) 44vw, (max-width: 1535px) 30vw, 22vw"
              />
            ),
          }))}
        >
          {showAllImages && hasMultipleImages && (
            <CarouselDots
              className={cn(
                // Absolute & placement (full-width bottom scrim, per design)
                // Ensure dots don't render behind images on iOS Safari during horizontal scroll.
                'absolute inset-x-0 bottom-0 z-40',
              )}
              carouselId={`listing-${listing.id}`}
            />
          )}

          {/* Desktop hover controls */}
          {showAllImages && hasMultipleImages && !isMapVariant && (
            <CarouselControls
              carouselId={`listing-${listing.id}`}
              className={cn(
                'absolute inset-0 px-3 z-20 pointer-events-none',
                'hidden lg:group-hover:flex',
              )}
              prevClassName={cn(
                `disabled:hidden`,
                'bg-bg-transparent',
                'border-0',
                'pointer-events-auto',
              )}
              nextClassName={cn(
                `disabled:hidden`,
                'bg-bg-transparent',
                'border-0',
                'pointer-events-auto',
                'ml-auto',
              )}
            />
          )}
        </Carousel>

        {/* Topmost overlay layer for tags + save (prevents iOS Safari z-index/paint glitches while carousel scrolls) */}
        <div className="absolute inset-0 z-50 pointer-events-none" style={overlayPaintStyle}>
          {/* Tags Overlay - Top Left */}
          <ListingCardTag listing={listing} className="pointer-events-auto" />
          {/* Save Button - Top Right */}
          <div className="absolute top-3 right-3 pointer-events-auto">
            <SaveListing
              listingId={listing.id}
              listingSlug={listing.slug}
              listing={listing}
              initialSaved={isSaved}
              className="shadow"
              {...(onSaveChange ? { onSaveChange } : {})}
            />
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div
        className={cn(
          // Layout for card content section
          'flex flex-col gap-1 w-full',
          // Map variant gets padding
          isMapVariant && 'px-4 pb-4',
        )}
      >
        {/* Top Row: Location + Property Details + Rating */}
        <div
          className={cn(
            // Row items & spacing
            'flex items-center gap-4 w-full',
          )}
        >
          {/* Location and Property Details */}
          <div
            className={cn(
              // Flex row, gap, grow/shrink
              'flex items-center gap-1 flex-1 min-w-0',
              // Typography
              'text-body-2-high text-primary',
            )}
          >
            <span className="shrink-0">{listing.neighborhood.name}</span>
            <span className="shrink-0 text-secondary">·</span>
            <span className="truncate">{propertyDetails}</span>
          </div>

          {/* Rating */}
          {hasReviews && (
            <Rating
              value={listing.buildingAverageRating as number}
              count={listing.buildingReviewCount as number}
            />
          )}
        </div>

        {/* Address and Urgency Tag */}
        <div className={cn('flex items-center gap-1 w-full')}>
          <p
            className={cn(
              // Typography & layout for address
              'text-body-2 text-secondary truncate min-w-0',
            )}
          >
            {listing.fullAddress?.split(',')[0] || ''}
          </p>
          {urgencyTag &&
            urgencyTag.Icon &&
            (() => {
              // Hoist the icon into a capitalized local so React reliably
              // treats it as a component. Member-expression JSX
              // (``<urgencyTag.Icon>``) can trip over undefined refs in
              // some bundler / minifier paths — the extra ``Icon`` guard
              // above keeps us from throwing "Element type is invalid"
              // if the svg export ever regresses.
              const UrgencyIcon = urgencyTag.Icon;
              return (
                <div className="flex shrink-0 items-center gap-1">
                  <span className="text-body-2 text-secondary">·</span>
                  <div className="flex items-center gap-1">
                    <UrgencyIcon className="w-4 h-4 shrink-0" />
                    <span className="text-caption-high text-coral-500">{urgencyTag.label}</span>
                  </div>
                </div>
              );
            })()}
        </div>

        {/* Price */}
        <div
          className={cn(
            // Row, spacing
            'flex items-center gap-1',
          )}
        >
          <ListingPromotionPrice
            listing={listing}
            screen="listing_card"
            grossClassName="text-caption"
            netClassName="text-body-1-high text-primary"
          />
        </div>
      </div>
    </ResponsiveLink>
  );
}
